import { create } from 'zustand';
import { API_BASE, WS_BASE } from '@/lib/config';
import { getToken } from '@/stores/authStore';
import type { ZoomOverviewData } from '@/components/report/overview-types';

// =============================================================================
// Types
// =============================================================================

export interface OverviewStep {
  name: string;
  label: string;
  status: 'pending' | 'active' | 'done' | 'error';
}

interface OverviewState {
  status: 'idle' | 'streaming' | 'ready' | 'error';
  data: Partial<ZoomOverviewData> | null;
  steps: OverviewStep[];
  progress: { completed: number; total: number };
  error: string | null;
  /** When the currently displayed report was generated (stored reports). */
  generatedAt: string | null;
  /** A background regeneration is streaming; current data stays visible. */
  isRefreshing: boolean;

  fetchOverview: (accountId: string, trigger?: 'initial' | 'manual') => Promise<void>;
  /**
   * Load the persisted report from the backend. Returns what it found so the
   * page can decide the next step (nothing stored → full streaming fetch;
   * stored but stale → silent refresh).
   */
  loadStored: () => Promise<{ found: boolean; stale: boolean }>;
  /**
   * Regenerate in the background: the visible report stays put while a fresh
   * stream buffers up; data swaps atomically when generation completes.
   */
  silentRefresh: (accountId: string) => Promise<void>;
  clear: () => void;
}

// Default steps shown during streaming
const DEFAULT_STEPS: OverviewStep[] = [
  { name: 'account', label: 'Account info', status: 'pending' },
  { name: 'users', label: 'User list', status: 'pending' },
  { name: 'settings', label: 'Security & meeting settings', status: 'pending' },
  { name: 'plans', label: 'Account plans', status: 'pending' },
  { name: 'roles', label: 'Roles', status: 'pending' },
  { name: 'groups', label: 'Groups', status: 'pending' },
  { name: 'usage', label: 'Usage metrics', status: 'pending' },
  { name: 'recordings', label: 'Cloud recordings', status: 'pending' },
  { name: 'phone', label: 'Phone settings', status: 'pending' },
  { name: 'rooms', label: 'Zoom Rooms', status: 'pending' },
  { name: 'marketplace', label: 'Marketplace apps', status: 'pending' },
  { name: 'archive', label: 'Archive stats', status: 'pending' },
  { name: 'meetingQuality', label: 'Meeting quality scores', status: 'pending' },
  { name: 'satisfaction', label: 'Client satisfaction', status: 'pending' },
  { name: 'phoneCallLogs', label: 'Phone call logs', status: 'pending' },
  { name: 'telephone', label: 'Telephone usage', status: 'pending' },
  { name: 'liveMeetings', label: 'Live meetings', status: 'pending' },
  { name: 'roomMetrics', label: 'Room quality metrics', status: 'pending' },
  { name: 'operationLogs', label: 'Operation logs', status: 'pending' },
  { name: 'enrichment', label: 'Participant details', status: 'pending' },
  { name: 'aiSummary', label: 'ZAC Insights', status: 'pending' },
];

// =============================================================================
// Store
// =============================================================================

export const useOverviewStore = create<OverviewState>()((set, get) => ({
  status: 'idle',
  data: null,
  steps: [],
  progress: { completed: 0, total: 0 },
  error: null,
  generatedAt: null,
  isRefreshing: false,

  loadStored: async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/zoom/report/stored`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      if (!res.ok) return { found: false, stale: false };
      const body = (await res.json()) as {
        found: boolean;
        data?: string;
        generatedAt?: string;
        stale?: boolean;
      };
      if (!body.found || !body.data) return { found: false, stale: false };
      const data = JSON.parse(body.data) as ZoomOverviewData;
      set({ status: 'ready', data, generatedAt: body.generatedAt ?? null, error: null });
      return { found: true, stale: !!body.stale };
    } catch {
      return { found: false, stale: false };
    }
  },

  silentRefresh: async (accountId: string) => {
    if (get().isRefreshing) return;
    set({ isRefreshing: true });
    try {
      const fresh = await streamOverviewBuffered(accountId);
      set({
        status: 'ready',
        data: fresh,
        generatedAt: new Date().toISOString(),
        isRefreshing: false,
        error: null,
      });
    } catch {
      // Keep showing the current report — a failed background refresh is
      // invisible by design; the next trigger retries.
      set({ isRefreshing: false });
    }
  },

  fetchOverview: async (accountId: string, trigger: 'initial' | 'manual' = 'manual') => {
    set({
      status: 'streaming',
      error: null,
      data: null,
      steps: DEFAULT_STEPS.map((s) => ({ ...s })),
      progress: { completed: 0, total: DEFAULT_STEPS.length },
    });

    try {
      await streamOverview(accountId, set, get, trigger);
    } catch {
      // Stream failed — fall back to REST
      const state = get();
      if (state.status !== 'ready') {
        await fetchOverviewRest(accountId, set);
      }
    }
  },

  clear: () => {
    set({
      status: 'idle',
      data: null,
      steps: [],
      progress: { completed: 0, total: 0 },
      error: null,
      generatedAt: null,
      isRefreshing: false,
    });
  },
}));

// =============================================================================
// WebSocket streaming
// =============================================================================

type SetFn = (partial: Partial<OverviewState> | ((state: OverviewState) => Partial<OverviewState>)) => void;
type GetFn = () => OverviewState;

function streamOverview(accountId: string, set: SetFn, get: GetFn, trigger = 'manual'): Promise<void> {
  return new Promise((resolve, reject) => {
    const token = getToken();
    if (!token) {
      reject(new Error('Not authenticated'));
      return;
    }

    const wsUrl = `${WS_BASE}/zoom/overview/stream?accountId=${encodeURIComponent(accountId)}&token=${encodeURIComponent(token)}&trigger=${encodeURIComponent(trigger)}`;
    let ws: WebSocket;

    try {
      ws = new WebSocket(wsUrl);
    } catch {
      reject(new Error('WebSocket connection failed'));
      return;
    }

    const timeout = setTimeout(() => {
      ws.close();
      reject(new Error('WebSocket connection timeout'));
    }, 5000);

    ws.onopen = () => {
      clearTimeout(timeout);
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        handleStreamMessage(msg, set, get);

        if (msg.type === 'done') {
          ws.close();
          resolve();
        } else if (msg.type === 'error') {
          ws.close();
          reject(new Error(msg.message));
        }
      } catch {
        // Ignore parse errors
      }
    };

    ws.onerror = () => {
      clearTimeout(timeout);
      reject(new Error('WebSocket error'));
    };

    ws.onclose = (event) => {
      clearTimeout(timeout);
      if (!event.wasClean && get().status === 'streaming') {
        reject(new Error('Connection closed'));
      }
    };
  });
}

function handleStreamMessage(msg: any, set: SetFn, get: GetFn) {
  switch (msg.type) {
    case 'step': {
      set((state) => ({
        steps: state.steps.map((s) =>
          s.name === msg.name ? { ...s, label: msg.label, status: msg.stepStatus } : s
        ),
        progress: { completed: msg.completed, total: msg.total },
      }));
      break;
    }

    case 'section': {
      // data arrives as a JSON string from the flat message interface
      const sectionData = typeof msg.data === 'string' ? JSON.parse(msg.data) : msg.data;
      set((state) => ({
        data: { ...state.data, [msg.name]: sectionData },
      }));
      break;
    }

    case 'done': {
      const state = get();
      const finalData = {
        ...state.data,
        fetchedAt: msg.fetchedAt,
        scope: msg.scope,
      } as ZoomOverviewData;

      set({
        status: 'ready',
        data: finalData,
        generatedAt: msg.fetchedAt ?? new Date().toISOString(),
        steps: state.steps.map((s) => ({
          ...s,
          status: s.status === 'pending' ? 'done' : s.status,
        })),
        progress: { completed: msg.total || state.progress.total, total: msg.total || state.progress.total },
      });
      break;
    }

    case 'error': {
      set({
        status: 'error',
        error: msg.message,
      });
      break;
    }
  }
}

// =============================================================================
// Silent (buffered) streaming — used by silentRefresh
// =============================================================================

/**
 * Same WebSocket stream as streamOverview, but sections accumulate in a local
 * buffer instead of the store — the visible report is untouched until the
 * stream completes and the caller swaps the data in atomically.
 */
function streamOverviewBuffered(accountId: string): Promise<ZoomOverviewData> {
  return new Promise((resolve, reject) => {
    const token = getToken();
    if (!token) {
      reject(new Error('Not authenticated'));
      return;
    }

    const wsUrl = `${WS_BASE}/zoom/overview/stream?accountId=${encodeURIComponent(accountId)}&token=${encodeURIComponent(token)}&trigger=auto`;
    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl);
    } catch {
      reject(new Error('WebSocket connection failed'));
      return;
    }

    const buffer: Record<string, unknown> = {};
    const timeout = setTimeout(() => {
      ws.close();
      reject(new Error('WebSocket connection timeout'));
    }, 5000);

    ws.onopen = () => clearTimeout(timeout);
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'section') {
          buffer[msg.name] = typeof msg.data === 'string' ? JSON.parse(msg.data) : msg.data;
        } else if (msg.type === 'done') {
          ws.close();
          resolve({ ...buffer, fetchedAt: msg.fetchedAt, scope: msg.scope } as ZoomOverviewData);
        } else if (msg.type === 'error') {
          ws.close();
          reject(new Error(msg.message));
        }
      } catch {
        // Ignore parse errors
      }
    };
    ws.onerror = () => {
      clearTimeout(timeout);
      reject(new Error('WebSocket error'));
    };
  });
}

// =============================================================================
// REST fallback
// =============================================================================

async function fetchOverviewRest(accountId: string, set: SetFn) {
  set({ status: 'streaming', error: null });

  try {
    const token = getToken();
    const res = await fetch(
      `${API_BASE}/zoom/overview?accountId=${encodeURIComponent(accountId)}`,
      {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'X-Tenant-ID': '',
        },
      }
    );

    if (!res.ok) {
      const body = await res.text();
      throw new Error(body || `Request failed: ${res.status}`);
    }

    const data: ZoomOverviewData = await res.json();
    set({ status: 'ready', data });
  } catch (err) {
    set({
      status: 'error',
      error: err instanceof Error ? err.message : 'Failed to fetch overview',
    });
  }
}
